import { useState, useEffect, useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { colors } from "../source/source.styles";
import ReportLoader from "../../Reports/Loader";
import ConversionMetrics from "./ConversionMetrics";
import FunnelStageConversion from "./FunnelStageConversion";
import DetailedSourcePerformanceWithFilter from "./DetailedSourcePerformanceWithFilter";
import DetailedSourceReport from "./DetailedSourceReport";
import SourceLeadDetails from "./SourceLeadDetails";
import EditSourceDetails from "./EditSourceDetails";
import LiveAnalytics from "./LiveAnalytics";
import RecentConversions from "./RecentConversions";
import RecentDropped from "./RecentDropped";
import { getConversionDroppedAnalyticsApi } from "../../../api/leads.api";
import dayjs from "dayjs";

const Conversions = ({ dateRange, onDetailedViewChange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [viewMode, setViewMode] = useState("dashboard"); // dashboard, report, leads, edit, analytics

  // Create a stable key for date range to trigger updates
  const dateRangeKey = useMemo(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      return `${dateRange.startDate.getTime()}-${dateRange.endDate.getTime()}`;
    }
    return "";
  }, [dateRange]);

  useEffect(() => {
    fetchConversionData();
  }, [dateRangeKey]);

  const fetchConversionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use dateRange if provided, otherwise default to current month
      const startDate = dateRange?.startDate
        ? dateRange.startDate.toISOString()
        : dayjs().startOf("month").toISOString();
      const endDate = dateRange?.endDate
        ? dateRange.endDate.toISOString()
        : dayjs().endOf("month").toISOString();

      const response = await getConversionDroppedAnalyticsApi({
        dimension: "both",
        startDate,
        endDate,
      });
      const apiData = response.data?.data;

      if (!apiData) {
        setMetricsData({
          conversionRate: 0,
          conversionRateTrend: 0,
          totalDropped: 0,
          overallDroppedRate: 0,
          totalWonDeals: 0,
          totalWonDealsTrend: 0,
        });
        setFunnelData({
          lead: 0,
          preConsultation: 0,
          postConsultation: 0,
          won: 0,
          totalDropped: 0,
          grade: { new: 0, cold: 0, hot: 0, warm: 0, dropped: 0 },
          preConsult: { qualified: 0, needAnalysis: 0, booked: 0, dropped: 0 },
          postConsult: {
            postConsult: 0,
            proposalShared: 0,
            negotiation: 0,
            treatmentBooked: 0,
            won: 0,
            dropped: 0,
          },
        });
        setPerformanceData([]);
        return;
      }

      const { summary, sources = [], services = [] } = apiData;

      // Calculate funnel stages from real-time data
      const totalGrade = sources.reduce((sum, s) => sum + s.grade.total, 0);
      const totalPreConsult = sources.reduce(
        (sum, s) => sum + s.preConsult.total,
        0,
      );
      const totalPostConsult = sources.reduce(
        (sum, s) => sum + s.postConsult.total,
        0,
      );
      const totalWon = summary.totalWon || 0;

      // Calculate detailed breakdown for funnel stages
      const gradeBreakdown = sources.reduce(
        (acc, s) => ({
          new: acc.new + (s.grade?.new || 0),
          cold: acc.cold + (s.grade?.cold || 0),
          hot: acc.hot + (s.grade?.hot || 0),
          warm: acc.warm + (s.grade?.warm || 0),
          dropped: acc.dropped + (s.grade?.dropped || 0),
        }),
        { new: 0, cold: 0, hot: 0, warm: 0, dropped: 0 },
      );

      const preConsultBreakdown = sources.reduce(
        (acc, s) => ({
          qualified: acc.qualified + (s.preConsult?.qualified || 0),
          needAnalysis: acc.needAnalysis + (s.preConsult?.needAnalysis || 0),
          booked: acc.booked + (s.preConsult?.booked || 0),
          dropped: acc.dropped + (s.preConsult?.dropped || 0),
        }),
        { qualified: 0, needAnalysis: 0, booked: 0, dropped: 0 },
      );

      const postConsultBreakdown = sources.reduce(
        (acc, s) => ({
          postConsult: acc.postConsult + (s.postConsult?.postConsult || 0),
          proposalShared:
            acc.proposalShared + (s.postConsult?.proposalShared || 0),
          negotiation: acc.negotiation + (s.postConsult?.negotiation || 0),
          treatmentBooked:
            acc.treatmentBooked + (s.postConsult?.treatmentBooked || 0),
          won: acc.won + (s.postConsult?.won || 0),
          dropped: acc.dropped + (s.postConsult?.dropped || 0),
        }),
        {
          postConsult: 0,
          proposalShared: 0,
          negotiation: 0,
          treatmentBooked: 0,
          won: 0,
          dropped: 0,
        },
      );

      const funnelStages = {
        lead: totalGrade,
        preConsultation: totalPreConsult,
        postConsultation: totalPostConsult,
        won: totalWon,
        totalDropped: summary.totalDropped || 0,
        grade: gradeBreakdown,
        preConsult: preConsultBreakdown,
        postConsult: postConsultBreakdown,
      };

      // Calculate metrics from real-time data
      const conversionRate = summary.overallConversionRate || 0;
      const totalDropped = summary.totalDropped || 0;
      const overallDroppedRate = summary.overallDroppedRate || 0;

      setMetricsData({
        conversionRate: Math.round(conversionRate * 10) / 10,
        conversionRateTrend: 0, // Trend would need historical data comparison
        totalDropped,
        overallDroppedRate: Math.round(overallDroppedRate * 10) / 10,
        totalWonDeals: totalWon,
        totalWonDealsTrend: 0,
      });

      setFunnelData(funnelStages);

      // Transform source performance data with conversion and dropped rates
      const performanceDataFromSources = sources.map((source) => ({
        name: source.source,
        totalLeads: source.totalLeads,
        conversionRate: source.conversionRate,
        conversionChange: 0, // Would need previous period data
        droppedRate: source.droppedRate,
        revenue: source.revenue,
        grade: source.grade,
        preConsult: source.preConsult,
        postConsult: source.postConsult,
      }));
      setPerformanceData(performanceDataFromSources);
    } catch (err) {
      console.error("Error fetching conversion data:", err);
      setError(err.message || "Failed to load conversion data");
    } finally {
      setLoading(false);
    }
  };

  const handleViewSourceReport = (source) => {
    setSelectedSource(source);
    setViewMode("report");
    if (onDetailedViewChange) {
      onDetailedViewChange(true);
    }
  };

  const handleViewLeadDetails = (source) => {
    setSelectedSource(source);
    setViewMode("leads");
    if (onDetailedViewChange) {
      onDetailedViewChange(true);
    }
  };

  const handleEditSource = (source) => {
    setSelectedSource(source);
    setViewMode("edit");
    if (onDetailedViewChange) {
      onDetailedViewChange(true);
    }
  };

  const handleViewAnalytics = (source) => {
    setSelectedSource(source);
    setViewMode("analytics");
    if (onDetailedViewChange) {
      onDetailedViewChange(true);
    }
  };

  const handleSaveSource = (formData) => {
    console.log("Saving source data:", formData);
    // TODO: API call to save source data
  };

  const handleBackFromReport = () => {
    setSelectedSource(null);
    setViewMode("dashboard");
    if (onDetailedViewChange) {
      onDetailedViewChange(false);
    }
  };

  if (loading) {
    return <ReportLoader />;
  }

  if (error) {
    return (
      <Box
        sx={{
          height: "calc(100vh - 200px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 400, p: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: colors.textPrimary,
              mb: 1,
            }}
          >
            Failed to Load Conversion Data
          </Typography>
          <Typography sx={{ fontSize: 14, color: colors.textSecondary, mb: 3 }}>
            {error}
          </Typography>
          <Box
            component="button"
            onClick={fetchConversionData}
            sx={{
              px: 3,
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              color: "#FFF",
              bgcolor: colors.primary,
              border: "none",
              borderRadius: 2,
              cursor: "pointer",
              "&:hover": {
                bgcolor: colors.primaryDark,
              },
            }}
          >
            Retry
          </Box>
        </Box>
      </Box>
    );
  }

  // Show detailed source report if a source is selected
  if (viewMode === "report" && selectedSource) {
    return (
      <DetailedSourceReport
        source={selectedSource}
        onBack={handleBackFromReport}
      />
    );
  }

  // Show lead details if view mode is leads
  if (viewMode === "leads" && selectedSource) {
    return (
      <SourceLeadDetails
        source={selectedSource}
        onBack={handleBackFromReport}
      />
    );
  }

  // Show edit source details if view mode is edit
  if (viewMode === "edit" && selectedSource) {
    return (
      <EditSourceDetails
        source={selectedSource}
        onBack={handleBackFromReport}
        onSave={handleSaveSource}
        onViewAnalytics={handleViewAnalytics}
      />
    );
  }

  // Show live analytics if view mode is analytics
  if (viewMode === "analytics" && selectedSource) {
    return (
      <LiveAnalytics source={selectedSource} onBack={handleBackFromReport} />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Conversion Metrics */}
      {metricsData && <ConversionMetrics data={metricsData} />}

      {/* Funnel Stage Conversion - Full Width */}
      {funnelData && <FunnelStageConversion data={funnelData} />}

      {/* Recent Conversions */}
      <RecentConversions limit={5} dateRange={dateRange} />

      {/* Recent Dropped */}
      <RecentDropped limit={5} dateRange={dateRange} />

      {/* Detailed Source Performance - Full Width with Filter */}
      {/* <DetailedSourcePerformanceWithFilter
        data={performanceData}
        onViewReport={handleViewSourceReport}
        onViewLeads={handleViewLeadDetails}
        onEditSource={handleEditSource}
      /> */}
    </Box>
  );
};

export default Conversions;
